import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import gsap from 'gsap';

/**
 * Hero-Grafik: Padel-Ball, der über einen schwebenden Court in Richtung
 * Horizont spielt – der Horizont steht für die Flensburger Förde als
 * Grenzgewässer zwischen Deutschland und Dänemark. Eine gestrichelte,
 * "fließende" Linie markiert die Grenze, links/rechts begleiten Partikel
 * in den Landesfarben.
 */

const HEX = {
  ballGold: 0xf2a83c,
  line: 0xffffff,
  water: 0x3b639f,
  waterDeep: 0x142a4d,
  deBlack: 0x1a1a1a,
  deRed: 0xdd0000,
  deGold: 0xffce00,
  dkRed: 0xc60c30,
  dkWhite: 0xffffff,
};

function createBallTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#F2A83C';
  ctx.fillRect(0, 0, size, size);

  const grad = ctx.createRadialGradient(size * 0.35, size * 0.3, 10, size * 0.5, size * 0.5, size * 0.75);
  grad.addColorStop(0, 'rgba(255,255,255,0.35)');
  grad.addColorStop(1, 'rgba(0,0,0,0.12)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle = 'rgba(255,255,255,0.65)';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(-size * 0.1, size * 0.12);
  ctx.bezierCurveTo(size * 0.4, size * 0.38, size * 0.4, size * 0.62, -size * 0.1, size * 0.88);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(size * 1.1, size * 0.12);
  ctx.bezierCurveTo(size * 0.6, size * 0.38, size * 0.6, size * 0.62, size * 1.1, size * 0.88);
  ctx.stroke();

  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 4;
  return tex;
}

function addLine(
  parent: THREE.Object3D,
  points: THREE.Vector3[],
  opts: { color?: number; opacity?: number; dashed?: boolean; dashSize?: number; gapSize?: number } = {}
): THREE.Line {
  const { color = HEX.line, opacity = 0.3, dashed = false, dashSize = 0.16, gapSize = 0.12 } = opts;
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = dashed
    ? new THREE.LineDashedMaterial({ color, transparent: true, opacity, dashSize, gapSize })
    : new THREE.LineBasicMaterial({ color, transparent: true, opacity });
  const line = new THREE.Line(geometry, material);
  if (dashed) line.computeLineDistances();
  parent.add(line);
  return line;
}

function buildFlagCluster(colors: number[], xSign: 1 | -1): THREE.Points {
  const count = 14;
  const positions = new Float32Array(count * 3);
  const colorAttr = new Float32Array(count * 3);
  const tmp = new THREE.Color();
  for (let i = 0; i < count; i++) {
    positions[i * 3] = xSign * (3.4 + Math.random() * 1.6);
    positions[i * 3 + 1] = -0.6 + Math.random() * 3;
    positions[i * 3 + 2] = -3 + Math.random() * 4;
    tmp.setHex(colors[i % colors.length]);
    colorAttr[i * 3] = tmp.r;
    colorAttr[i * 3 + 1] = tmp.g;
    colorAttr[i * 3 + 2] = tmp.b;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colorAttr, 3));
  const material = new THREE.PointsMaterial({
    size: 0.09,
    vertexColors: true,
    transparent: true,
    opacity: 0.55,
    sizeAttenuation: true,
    depthWrite: false,
  });
  return new THREE.Points(geometry, material);
}

export default function HeroScene3D() {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
    } catch {
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    const cameraBase = new THREE.Vector3(0.4, 1.6, 7.4);
    camera.position.copy(cameraBase);
    camera.lookAt(0, 0.2, 0);

    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(3, 5, 4);
    scene.add(key);

    /* ── Horizont / Förde (Wasserebene) ── */
    const waterGeo = new THREE.PlaneGeometry(30, 14, 40, 20);
    const waterMat = new THREE.MeshStandardMaterial({
      color: HEX.water,
      transparent: true,
      opacity: 0.16,
      roughness: 0.6,
      metalness: 0.1,
      side: THREE.DoubleSide,
    });
    const water = new THREE.Mesh(waterGeo, waterMat);
    water.rotation.x = -Math.PI / 2;
    water.position.set(0, -1.9, -4);
    scene.add(water);
    const waterPos = waterGeo.attributes.position as THREE.BufferAttribute;
    const waterBase = Float32Array.from(waterPos.array);

    /* ── Grenzlinie DE/DK: gestrichelte, leicht gewellte Linie am Horizont ── */
    const borderPts: THREE.Vector3[] = [];
    for (let i = 0; i <= 40; i++) {
      const x = -8 + (i / 40) * 16;
      const y = Math.sin(i * 0.6) * 0.12;
      borderPts.push(new THREE.Vector3(x, y, 0));
    }
    const borderGroup = new THREE.Group();
    borderGroup.position.set(0, -0.15, -2.2);
    const borderLine = addLine(borderGroup, borderPts, { color: HEX.line, opacity: 0.4, dashed: true, dashSize: 0.22, gapSize: 0.16 });
    scene.add(borderGroup);
    const borderDist = borderLine.geometry.attributes.lineDistance as THREE.BufferAttribute;
    const borderDistBase = Float32Array.from(borderDist.array);

    /* ── Court (schwebend, leicht gekippt) ── */
    const court = new THREE.Group();
    court.rotation.x = -1.18;
    court.rotation.z = -0.05;
    court.position.set(-0.3, -0.55, -0.4);
    scene.add(court);

    const W = 1.7;
    const D = 2.7;
    addLine(court, [
      new THREE.Vector3(-W, 0, -D), new THREE.Vector3(W, 0, -D),
      new THREE.Vector3(W, 0, D), new THREE.Vector3(-W, 0, D),
      new THREE.Vector3(-W, 0, -D),
    ], { opacity: 0.32 });
    addLine(court, [new THREE.Vector3(-W, 0.35, -0.9), new THREE.Vector3(W, 0.35, -0.9)], { opacity: 0.22 });
    addLine(court, [new THREE.Vector3(-W, 0.35, 0.9), new THREE.Vector3(W, 0.35, 0.9)], { opacity: 0.22 });
    addLine(court, [new THREE.Vector3(0, 0, -D), new THREE.Vector3(0, 0, -0.9)], { opacity: 0.2 });
    addLine(court, [new THREE.Vector3(0, 0, D), new THREE.Vector3(0, 0, 0.9)], { opacity: 0.2 });
    addLine(court, [new THREE.Vector3(-W, 0.5, 0), new THREE.Vector3(W, 0.5, 0)], {
      color: HEX.ballGold, opacity: 0.55, dashed: true, dashSize: 0.12, gapSize: 0.08,
    });
    const postGeo = new THREE.SphereGeometry(0.045, 12, 12);
    const postMat = new THREE.MeshBasicMaterial({ color: HEX.ballGold, transparent: true, opacity: 0.55 });
    const postL = new THREE.Mesh(postGeo, postMat); postL.position.set(-W, 0.5, 0); court.add(postL);
    const postR = new THREE.Mesh(postGeo, postMat); postR.position.set(W, 0.5, 0); court.add(postR);

    /* ── Padel-Ball ── */
    const ballTexture = createBallTexture();
    const ball = new THREE.Mesh(
      new THREE.SphereGeometry(0.16, 32, 32),
      new THREE.MeshStandardMaterial({ map: ballTexture, roughness: 0.45, metalness: 0.05 })
    );
    ball.position.set(-1.1, 0.25, -1.8);
    court.add(ball);

    const glow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: (() => {
          const c = document.createElement('canvas');
          c.width = c.height = 128;
          const g = c.getContext('2d')!;
          const rg = g.createRadialGradient(64, 64, 0, 64, 64, 64);
          rg.addColorStop(0, 'rgba(242,168,60,0.55)');
          rg.addColorStop(1, 'rgba(242,168,60,0)');
          g.fillStyle = rg;
          g.fillRect(0, 0, 128, 128);
          return new THREE.CanvasTexture(c);
        })(),
        transparent: true,
        depthWrite: false,
      })
    );
    glow.scale.set(0.9, 0.9, 0.9);
    ball.add(glow);

    /* ── Flaggen-Partikel (DE links, DK rechts) ── */
    const deParticles = buildFlagCluster([HEX.deBlack, HEX.deRed, HEX.deGold], -1);
    const dkParticles = buildFlagCluster([HEX.dkRed, HEX.dkWhite], 1);
    const deMat = deParticles.material as THREE.PointsMaterial;
    const dkMat = dkParticles.material as THREE.PointsMaterial;
    scene.add(deParticles, dkParticles);
    const deBase = Float32Array.from((deParticles.geometry.attributes.position as THREE.BufferAttribute).array);
    const dkBase = Float32Array.from((dkParticles.geometry.attributes.position as THREE.BufferAttribute).array);

    /* ── Resize ── */
    const resize = () => {
      const { clientWidth: w, clientHeight: h } = mount;
      if (w === 0 || h === 0) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);

    /* ── Sichtbarkeit: nur rendern wenn Hero im Viewport & Tab aktiv ── */
    let inView = true;
    const io = new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; }, { threshold: 0.05 });
    io.observe(mount);

    /* ── Maus-Parallax ── */
    const mouse = { x: 0, y: 0 };
    const setCamX = gsap.quickTo(camera.position, 'x', { duration: 1.4, ease: 'power3.out' });
    const setCamY = gsap.quickTo(camera.position, 'y', { duration: 1.4, ease: 'power3.out' });
    const onMouseMove = (e: MouseEvent) => {
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = (e.clientY / window.innerHeight) * 2 - 1;
      setCamX(cameraBase.x + mouse.x * 0.5);
      setCamY(cameraBase.y - mouse.y * 0.3);
    };
    if (!reduceMotion) window.addEventListener('mousemove', onMouseMove);

    /* ── Timelines ── */
    const timelines: gsap.core.Timeline[] = [];

    const introTl = gsap.timeline();
    introTl
      .from(mount, { opacity: 0, duration: 1.1, ease: 'power2.out' })
      .from(court.scale, { x: 0.85, y: 0.85, z: 0.85, duration: 1.2, ease: 'power3.out' }, '<')
      .from(ball.scale, { x: 0, y: 0, z: 0, duration: 0.7, ease: 'back.out(2)' }, '-=0.5')
      .from([deMat, dkMat], { opacity: 0, duration: 1, ease: 'power2.out' }, '-=0.8');

    if (!reduceMotion) {
      const rallyTl = gsap.timeline({ repeat: -1 });
      const hops: Array<[number, number]> = [[1.1, -0.6], [-1.1, 0.9], [0.6, -1.8]];
      hops.forEach(([x, z]) => {
        rallyTl
          .to(ball.position, { x, z, y: 0.75, duration: 0.55, ease: 'power1.out' })
          .to(ball.position, { y: 0.12, duration: 0.55, ease: 'power1.in' });
      });
      rallyTl.to(ball.position, { x: -1.1, z: -1.8, y: 0.25, duration: 0.6, ease: 'power2.inOut' });
      timelines.push(rallyTl);

      gsap.to(ball.rotation, { x: `+=${Math.PI * 4}`, y: `+=${Math.PI * 6}`, duration: 6, repeat: -1, ease: 'none' });
      gsap.to(court.rotation, { z: '-=0.03', duration: 5, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    }

    /* ── Render-Loop ── */
    const clock = new THREE.Clock();
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (document.hidden || !inView) return;
      const t = clock.getElapsedTime();

      if (!reduceMotion) {
        const arr = waterPos.array as Float32Array;
        for (let i = 0; i < arr.length; i += 3) {
          arr[i + 2] = waterBase[i + 2] + Math.sin(t * 0.6 + waterBase[i] * 0.5) * 0.06
            + Math.cos(t * 0.4 + waterBase[i + 1] * 0.4) * 0.04;
        }
        waterPos.needsUpdate = true;

        const distArr = borderDist.array as Float32Array;
        for (let i = 0; i < distArr.length; i++) distArr[i] = borderDistBase[i] + t * 0.5;
        borderDist.needsUpdate = true;

        const deArr = (deParticles.geometry.attributes.position as THREE.BufferAttribute).array as Float32Array;
        for (let i = 0; i < deArr.length; i += 3) {
          deArr[i + 1] = deBase[i + 1] + Math.sin(t * 0.8 + i) * 0.18;
        }
        (deParticles.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;

        const dkArr = (dkParticles.geometry.attributes.position as THREE.BufferAttribute).array as Float32Array;
        for (let i = 0; i < dkArr.length; i += 3) {
          dkArr[i + 1] = dkBase[i + 1] + Math.sin(t * 0.7 + i * 1.3) * 0.18;
        }
        (dkParticles.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
      }

      camera.lookAt(0, 0.2, 0);
      renderer.render(scene, camera);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMouseMove);
      resizeObserver.disconnect();
      io.disconnect();
      introTl.kill();
      timelines.forEach((tl) => tl.kill());
      gsap.killTweensOf([ball.rotation, court.rotation, camera.position]);

      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof THREE.Points) {
          obj.geometry.dispose();
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => {
            const map = (m as THREE.MeshStandardMaterial).map;
            if (map) map.dispose();
            m.dispose();
          });
        } else if (obj instanceof THREE.Sprite) {
          obj.material.map?.dispose();
          obj.material.dispose();
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className="absolute inset-0 pointer-events-none select-none"
      aria-hidden="true"
    />
  );
}
